// FE#335 — "My Status" as a compose recipient. A scheduled Status is composed
// exactly like a normal scheduled message (same editor, media, date/time,
// recurrence); the ONLY difference is the recipient — the status@broadcast
// sentinel. Status is mutually exclusive with chats/groups: a Status post has
// no per-recipient target, so selecting it replaces any other recipients (and
// vice-versa). MVP posts to the account's DEFAULT Status audience — the
// audience selector is HIGH-priority Phase 2 (statusJidList must be plumbed
// through the shared createStatusTool contract).
import { chatKindOf } from '@bt-common/schemas/wa/chat-kind';
import type { ScheduleRecipient } from '@common-reducers/AddScheduledMessageButtonReducer';
import { WHATSAPP_STATUS_TARGET_ID } from './status-scheduling';

/** Synthetic recipient representing the user's own WhatsApp Status. */
export const STATUS_RECIPIENT: ScheduleRecipient = {
  id: WHATSAPP_STATUS_TARGET_ID,
  name: 'My Status',
  formattedTitle: 'My Status',
};

/** Shown next to the Status option so nobody is surprised by reach. Until the
 *  Phase-2 audience selector ships, a Status posts to the account default. */
export const STATUS_AUDIENCE_LABEL = 'Posts to your default Status audience';

const idOf = (r: unknown): string =>
  String((r as { id?: unknown } | null | undefined)?.id ?? '').trim();

export function isStatusRecipient(r: unknown): boolean {
  return chatKindOf(idOf(r)) === 'status';
}

export function hasStatusRecipient(list: readonly ScheduleRecipient[] | undefined): boolean {
  return (list ?? []).some(isStatusRecipient);
}

/** Exactly the Status recipient — used when the user picks "Schedule a Status". */
export function selectStatusOnly(): ScheduleRecipient[] {
  return [STATUS_RECIPIENT];
}

/** Add a recipient with Status↔chat exclusivity, preserving the reducer's
 *  dedupe-by-id. Adding Status replaces everything; adding a normal contact
 *  drops any Status entry first, then appends (deduped). */
export function applyAddRecipient(
  list: readonly ScheduleRecipient[] | undefined,
  incoming: ScheduleRecipient,
): ScheduleRecipient[] {
  if (isStatusRecipient(incoming)) return selectStatusOnly();
  const withoutStatus = (list ?? []).filter((r) => !isStatusRecipient(r));
  const incomingId = idOf(incoming);
  if (withoutStatus.some((r) => idOf(r) === incomingId)) return withoutStatus.slice();
  return [...withoutStatus, incoming];
}
